import React, { useState } from 'react';
import { TrainingProgramService } from '../services/trainingProgram';
import { WorkoutService } from '../services/workout';
import { ExerciseCatalogService } from '../services/exerciseCatalog';
import { WorkoutHistoryService } from '../services/workoutHistory';
import { AnalyticsService } from '../services/analytics';
import { ValidationService } from '../services/validation';
import { LocalizationService } from '../services/localization';

interface WorkflowTest {
  name: string;
  description: string;
  status: 'pending' | 'running' | 'pass' | 'fail';
  error?: string;
  duration?: number;
}

/**
 * End-to-end workflow testing component
 * Tests complete user workflows from start to finish
 */
export const WorkflowTestSuite: React.FC = () => {
  const [tests, setTests] = useState<WorkflowTest[]>([
    {
      name: 'Создание программы тренировок',
      description: 'Создание новой программы с упражнениями',
      status: 'pending'
    },
    {
      name: 'Выполнение тренировки',
      description: 'Полный цикл выполнения тренировки',
      status: 'pending'
    },
    {
      name: 'Просмотр истории',
      description: 'Загрузка и отображение истории тренировок',
      status: 'pending'
    },
    {
      name: 'Аналитика прогресса',
      description: 'Расчет и отображение аналитики',
      status: 'pending'
    },
    {
      name: 'Система комментариев',
      description: 'Добавление и отображение комментариев',
      status: 'pending'
    },
    {
      name: 'Валидация данных',
      description: 'Проверка валидации форм и данных',
      status: 'pending'
    },
    {
      name: 'Локализация',
      description: 'Проверка русской локализации',
      status: 'pending'
    }
  ]);

  const updateTestStatus = (index: number, status: WorkflowTest['status'], error?: string, duration?: number) => {
    setTests(prev => prev.map((test, i) => 
      i === index ? { ...test, status, error, duration } : test
    ));
  };

  const runWorkflowTest = async (testIndex: number) => {
    const startTime = Date.now();
    updateTestStatus(testIndex, 'running');

    try {
      switch (testIndex) {
        case 0: // Создание программы тренировок
          await testProgramCreation();
          break;
        case 1: // Выполнение тренировки
          await testWorkoutExecution();
          break;
        case 2: // Просмотр истории
          await testWorkoutHistory();
          break;
        case 3: // Аналитика прогресса
          await testAnalytics();
          break;
        case 4: // Система комментариев
          await testCommentSystem();
          break;
        case 5: // Валидация данных
          await testDataValidation();
          break;
        case 6: // Локализация
          await testLocalization();
          break;
      }
      
      const duration = Date.now() - startTime;
      updateTestStatus(testIndex, 'pass', undefined, duration);
    } catch (error) {
      const duration = Date.now() - startTime;
      updateTestStatus(testIndex, 'fail', error instanceof Error ? error.message : 'Неизвестная ошибка', duration);
    }
  };

  const testProgramCreation = async () => {
    // Test program creation workflow
    const exercises = await ExerciseCatalogService.getExercises();
    if (exercises.length === 0) {
      throw new Error('Каталог упражнений пуст');
    }

    const testProgram = {
      name: `Тестовая программа ${Date.now()}`,
      description: 'Программа для тестирования',
      workouts: [
        {
          name: 'Тренировка А',
          exercises: exercises.slice(0, 3).map((exercise, index) => ({
            exercise_id: exercise.id,
            order_index: index,
            target_sets: 3,
            target_reps: '8-12',
            target_weight: 50,
            rest_seconds: 90
          }))
        }
      ]
    };

    const program = await TrainingProgramService.createProgram(testProgram);
    if (!program) {
      throw new Error('Не удалось создать программу');
    }

    // Test setting as active
    await TrainingProgramService.setActiveProgram(program.id);
    const activeProgram = await TrainingProgramService.getActiveProgram();
    if (!activeProgram || activeProgram.id !== program.id) {
      throw new Error('Не удалось установить активную программу');
    }
  };

  const testWorkoutExecution = async () => {
    // Test complete workout execution
    const activeProgram = await TrainingProgramService.getActiveProgram();
    if (!activeProgram || !activeProgram.workouts.length) {
      throw new Error('Нет активной программы для тестирования');
    }

    const workout = activeProgram.workouts[0];
    const workoutSession = await WorkoutService.startWorkout(workout.id);
    if (!workoutSession) {
      throw new Error('Не удалось начать тренировку');
    }

    // Simulate logging sets
    for (const exercise of workout.exercises) {
      for (let setIndex = 0; setIndex < 3; setIndex++) {
        await WorkoutService.logSet(workoutSession.id, exercise.exercise_id, {
          weight: 50 + setIndex * 2.5,
          reps: 10 - setIndex,
          rpe: 7 + setIndex,
          completed: true
        });
      }
    }

    // Complete workout
    await WorkoutService.completeWorkout(workoutSession.id, {
      overall_notes: 'Тестовая тренировка завершена успешно'
    });

    // Verify workout was saved
    const completedWorkout = await WorkoutService.getWorkout(workoutSession.id);
    if (!completedWorkout || !completedWorkout.completed_at) {
      throw new Error('Тренировка не была сохранена корректно');
    }
  };

  const testWorkoutHistory = async () => {
    // Test workout history loading and filtering
    const history = await WorkoutHistoryService.getWorkoutHistory();
    if (history.length === 0) {
      throw new Error('История тренировок пуста');
    }

    // Test filtering by date range
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const recentHistory = await WorkoutHistoryService.getWorkoutHistory({
      startDate: oneWeekAgo,
      endDate: new Date()
    });

    // Test getting workout details
    const firstWorkout = history[0];
    const workoutDetails = await WorkoutHistoryService.getWorkoutDetails(firstWorkout.id);
    if (!workoutDetails) {
      throw new Error('Не удалось загрузить детали тренировки');
    }
  };

  const testAnalytics = async () => {
    // Test analytics calculations
    const analytics = new AnalyticsService();
    
    // Test overall stats
    const overallStats = await analytics.getOverallStats();
    if (!overallStats) {
      throw new Error('Не удалось получить общую статистику');
    }

    // Test exercise analytics
    const exercises = await ExerciseCatalogService.getExercises();
    if (exercises.length > 0) {
      const exerciseAnalytics = await analytics.getExerciseAnalytics(exercises[0].id);
      // Analytics might be empty for new exercises, so we just check it doesn't throw
    }

    // Test strength progress
    const strengthProgress = await analytics.getStrengthProgress();
    // Progress might be empty for new users, so we just check it doesn't throw
  };

  const testCommentSystem = async () => {
    // Test comment functionality (using mock data since we need a real workout)
    const history = await WorkoutHistoryService.getWorkoutHistory();
    if (history.length === 0) {
      throw new Error('Нет тренировок для тестирования комментариев');
    }

    const workout = history[0];
    const workoutDetails = await WorkoutHistoryService.getWorkoutDetails(workout.id);
    if (!workoutDetails) {
      throw new Error('Не удалось загрузить детали тренировки');
    }

    // Check if workout has comments (either overall or set comments)
    const hasComments = workoutDetails.overall_notes || 
      workoutDetails.sets.some(set => set.notes);
    
    // This is more of a structural test since we can't easily add comments in this context
    if (workoutDetails.sets.length === 0) {
      throw new Error('Тренировка не содержит подходов');
    }
  };

  const testDataValidation = async () => {
    // Test validation service
    const validation = new ValidationService();

    // Test program name validation
    const validProgramName = validation.validateProgramName('Тестовая программа');
    if (!validProgramName.isValid) {
      throw new Error('Валидация корректного названия программы не прошла');
    }

    const invalidProgramName = validation.validateProgramName('');
    if (invalidProgramName.isValid) {
      throw new Error('Валидация пустого названия программы должна была провалиться');
    }

    // Test set data validation
    const validSetData = validation.validateSetData({
      weight: 50,
      reps: 10,
      rpe: 7,
      completed: true
    });
    if (!validSetData.isValid) {
      throw new Error('Валидация корректных данных подхода не прошла');
    }

    const invalidSetData = validation.validateSetData({
      weight: -10,
      reps: 0,
      rpe: 15,
      completed: true
    });
    if (invalidSetData.isValid) {
      throw new Error('Валидация некорректных данных подхода должна была провалиться');
    }

    // Test business rules
    const programs = await TrainingProgramService.getPrograms();
    const activePrograms = programs.filter(p => p.is_active);
    if (activePrograms.length > 1) {
      throw new Error('Найдено более одной активной программы - нарушение бизнес-правила');
    }
  };

  const testLocalization = async () => {
    // Test localization service
    const localization = new LocalizationService();

    // Test text translation
    const translatedText = localization.getText('common.save');
    if (!translatedText || translatedText === 'common.save') {
      throw new Error('Локализация текста не работает');
    }

    // Test date formatting
    const testDate = new Date('2024-01-15T10:30:00');
    const formattedDate = localization.formatDate(testDate);
    if (!formattedDate.includes('15') || !formattedDate.includes('01')) {
      throw new Error('Форматирование даты не работает корректно');
    }

    // Test number formatting
    const formattedNumber = localization.formatNumber(1234.56);
    if (!formattedNumber.includes('1') || !formattedNumber.includes('234')) {
      throw new Error('Форматирование чисел не работает корректно');
    }

    // Test pluralization
    const pluralForm = localization.getPlural(5, 'workout');
    if (!pluralForm.includes('тренировок')) {
      throw new Error('Плюрализация не работает корректно');
    }

    // Test Cyrillic validation
    const cyrillicValidation = localization.validateCyrillic('Тестовый текст');
    if (!cyrillicValidation.isValid) {
      throw new Error('Валидация кириллицы не работает');
    }

    const nonCyrillicValidation = localization.validateCyrillic('Test text');
    if (nonCyrillicValidation.isValid) {
      throw new Error('Валидация должна отклонять не-кириллический текст');
    }
  };

  const runAllTests = async () => {
    for (let i = 0; i < tests.length; i++) {
      await runWorkflowTest(i);
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const getStatusIcon = (status: WorkflowTest['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'running': return '🔄';
      case 'pass': return '✅';
      case 'fail': return '❌';
    }
  };

  const getStatusColor = (status: WorkflowTest['status']) => {
    switch (status) {
      case 'pending': return 'text-tg-secondary';
      case 'running': return 'text-blue-600';
      case 'pass': return 'text-green-600';
      case 'fail': return 'text-red-600';
    }
  };

  const passCount = tests.filter(t => t.status === 'pass').length;
  const failCount = tests.filter(t => t.status === 'fail').length;
  const runningCount = tests.filter(t => t.status === 'running').length;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">🔄 Тестирование рабочих процессов</h1>
        <p className="text-tg-secondary">
          Проверка полных пользовательских сценариев
        </p>
      </div>

      {/* Test Summary */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4">Сводка тестирования</h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{passCount}</div>
            <div className="text-sm text-tg-secondary">Пройдено</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{failCount}</div>
            <div className="text-sm text-tg-secondary">Ошибки</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{runningCount}</div>
            <div className="text-sm text-tg-secondary">Выполняется</div>
          </div>
        </div>
        
        <button 
          onClick={runAllTests}
          disabled={runningCount > 0}
          className="btn-primary w-full"
        >
          {runningCount > 0 ? 'Тестирование...' : 'Запустить все тесты'}
        </button>
      </div>

      {/* Individual Tests */}
      <div className="space-y-4">
        {tests.map((test, index) => (
          <div key={index} className="card">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start space-x-3">
                <span className="text-xl">{getStatusIcon(test.status)}</span>
                <div>
                  <h3 className="font-semibold">{test.name}</h3>
                  <p className="text-sm text-tg-secondary">{test.description}</p>
                </div>
              </div>
              <button
                onClick={() => runWorkflowTest(index)}
                disabled={test.status === 'running'}
                className="btn-secondary text-sm"
              >
                {test.status === 'running' ? 'Выполняется...' : 'Запустить'}
              </button>
            </div>
            
            <div className={`text-sm ${getStatusColor(test.status)}`}>
              {test.status === 'pass' && `✅ Тест пройден${test.duration ? ` за ${test.duration}мс` : ''}`}
              {test.status === 'fail' && `❌ Ошибка: ${test.error}`}
              {test.status === 'running' && '🔄 Выполняется...'}
              {test.status === 'pending' && '⏳ Ожидает запуска'}
            </div>
          </div>
        ))}
      </div>

      {/* Test Guidelines */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold mb-4">Рекомендации по тестированию</h2>
        <div className="space-y-3 text-sm">
          <div>
            <strong>Перед тестированием:</strong>
            <ul className="list-disc list-inside ml-4 mt-1 text-tg-secondary">
              <li>Убедитесь, что база данных настроена</li>
              <li>Проверьте наличие тестовых данных в каталоге упражнений</li>
              <li>Убедитесь в стабильном интернет-соединении</li>
            </ul>
          </div>
          
          <div>
            <strong>Интерпретация результатов:</strong>
            <ul className="list-disc list-inside ml-4 mt-1 text-tg-secondary">
              <li>✅ Зеленый - тест прошел успешно</li>
              <li>❌ Красный - обнаружена ошибка, требует исправления</li>
              <li>🔄 Синий - тест выполняется</li>
              <li>⏳ Серый - тест ожидает запуска</li>
            </ul>
          </div>
          
          <div>
            <strong>При ошибках:</strong>
            <ul className="list-disc list-inside ml-4 mt-1 text-tg-secondary">
              <li>Проверьте консоль браузера для дополнительной информации</li>
              <li>Убедитесь в корректности настроек базы данных</li>
              <li>Проверьте сетевое соединение</li>
              <li>Попробуйте запустить тест повторно</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTestSuite;